import type {ReactNode} from 'react'
import type {PendingReview} from '../../../../stores/slices/pending-review'
import {PullRequestState} from '../../../../types'
import {PendingBatch, shouldShowPendingBatch} from '../batch/PendingBatch'
import {ClosedPR, shouldShowClosedPR} from '../pull-request-state/ClosedPR'
import {MergedPR, shouldShowMergedPR} from '../pull-request-state/MergedPR'
import {DraftPR, shouldShowDraftPR} from '../pull-request-state/DraftPR'
import {FailingChecks} from '../mergeability/FailingChecks'
import {shouldShowMergeConflicts, MergeConflicts} from '../mergeability/MergeConflicts'
import {selectFailedRequiredChecks} from '../mergeability/use-failed-checks-data'
import {useMergeabilityData, type MergeabilityData} from '../mergeability/use-mergeability-data'
import {ReviewPending, shouldShowReviewPending} from '../reviewer/ReviewPending'
import {ReviewRequested, shouldShowReviewRequested} from '../reviewer/ReviewRequested'
import {ChangesSinceLastReview, shouldShowChangesSinceLastReview} from '../reviewer/ChangesSinceLastReview'
import {ReviewApproved, shouldShowReviewApproved} from '../reviewer/ReviewApproved'
import {FeedbackOpen, shouldShowFeedbackOpen} from '../reviewer/FeedbackOpen'
import {CommentsResolved, shouldShowCommentsResolved} from '../reviewer/CommentsResolved'
import {useReviewRequestedData, type ReviewRequestedData} from '../reviewer/use-review-requested-data'
import {usePullRequestState} from '../../../../hooks/use-pull-request-state'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {usePendingReviews, useUnifiedBatchCount} from '../../../../stores/layout-selectors'
import {useReviewData} from '../reviewer/use-review-data'
import {useReviewThreadsStartedByCurrentUser} from '../reviewer/use-review-threads-started-by-current-user'
import type {CompletedReview} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {useHeaderPageData} from '../../../../page-data/loaders/use-header-page-data'
import {QueuedPR, shouldShowQueuedPR} from '../pull-request-state/QueuedPR'
import {StackClosed, shouldShowStackClosed} from '../stack/StackClosed'
import {StackReviewPreviousFirst, shouldShowStackReviewPreviousFirst} from '../stack/StackReviewPreviousFirst'
import {useDownstackPendingReviewRequests, type StackPullRequest} from '../stack/use-downstack-pending-review-request'
import type {StackData} from '../../../../page-data/payloads/header'

type ReviewerForYouActionInput = {
  mergeabilityData: MergeabilityData | null
  pullRequestState: PullRequestState
  pullRequestHeadSha: string
  pullRequestNumber?: number
  stack?: StackData | null
  pendingReview: PendingReview | undefined
  currentUserLogin: string | undefined
  unifiedBatchEnabled: boolean
  batchCount: number
  reviewRequestedData: ReviewRequestedData | null
  review?: CompletedReview
  reviewThreads?: Thread[]
  downstackPendingReviewPullRequests?: StackPullRequest[]
}

type LazyAction = {
  action: ReactNode
  matches: () => boolean
}

export function useReviewerDecider() {
  const mergeabilityData = useMergeabilityData()
  const pullRequestState = usePullRequestState()
  const pendingReview = usePendingReviews()
  const currentUserLogin = getCurrentUserLogin()
  const unifiedBatchEnabled = isFeatureEnabled('unified_batch_pr_comments')
  const batchCount = useUnifiedBatchCount()
  const reviewRequested = useReviewRequestedData()
  const review = useReviewData()
  const reviewThreads = useReviewThreadsStartedByCurrentUser()
  const {pullRequest, stack} = useHeaderPageData().data
  const downstackPendingReviewRequests = useDownstackPendingReviewRequests(stack, pullRequest.number)

  if (reviewRequested.queryStatus !== 'success' || review.queryStatus !== 'success') return null

  return getReviewerForYouAction({
    mergeabilityData,
    pullRequestState,
    pullRequestHeadSha: pullRequest.headSha,
    pullRequestNumber: pullRequest.number,
    stack,
    pendingReview,
    currentUserLogin,
    unifiedBatchEnabled,
    batchCount,
    reviewRequestedData: reviewRequested.data,
    review: review.data ?? undefined,
    reviewThreads,
    downstackPendingReviewPullRequests: downstackPendingReviewRequests.data,
  })
}

export function getReviewerForYouAction({
  mergeabilityData,
  pullRequestState,
  pullRequestHeadSha,
  pullRequestNumber = 0,
  stack = null,
  pendingReview,
  currentUserLogin,
  unifiedBatchEnabled,
  batchCount,
  reviewRequestedData,
  review,
  reviewThreads = [],
  downstackPendingReviewPullRequests = [],
}: ReviewerForYouActionInput): ReactNode {
  const decisionTree: LazyAction[] = [
    {
      action: <ClosedPR variant="embedded" userRole="reviewer" />,
      matches: () => shouldShowClosedPR(pullRequestState),
    },
    {
      action: <MergedPR variant="embedded" userRole="reviewer" />,
      matches: () => shouldShowMergedPR(pullRequestState),
    },
    {
      action: <DraftPR variant="embedded" userRole="reviewer" />,
      matches: () => shouldShowDraftPR(pullRequestState),
    },
    {
      action: <StackClosed />,
      matches: () => shouldShowStackClosed(stack, pullRequestNumber),
    },
    {
      action: <QueuedPR variant="embedded" />,
      matches: () => shouldShowQueuedPR(mergeabilityData),
    },
    {
      action: <FailingChecks variant="embedded" />,
      matches: () => Boolean(mergeabilityData && selectFailedRequiredChecks(mergeabilityData.statusChecks).length),
    },
    {action: <MergeConflicts variant="embedded" />, matches: () => shouldShowMergeConflicts(mergeabilityData)},
    {action: <FeedbackOpen variant="embedded" />, matches: () => shouldShowFeedbackOpen(review, reviewThreads)},
    {
      action: <ChangesSinceLastReview variant="embedded" />,
      matches: () => shouldShowChangesSinceLastReview(review, pullRequestHeadSha),
    },
    {
      action: <StackReviewPreviousFirst />,
      matches: () => shouldShowStackReviewPreviousFirst(downstackPendingReviewPullRequests),
    },
    {
      action: <ReviewPending variant="embedded" />,
      matches: () => shouldShowReviewPending(pendingReview, currentUserLogin),
    },
    {
      action: <PendingBatch variant="embedded" />,
      matches: () => shouldShowPendingBatch(unifiedBatchEnabled, batchCount),
    },
    {action: <ReviewRequested variant="embedded" />, matches: () => shouldShowReviewRequested(reviewRequestedData)},
    {
      action: <CommentsResolved />,
      matches: () => shouldShowCommentsResolved(review, reviewThreads),
    },
    {
      action: <ReviewApproved />,
      matches: () => shouldShowReviewApproved(review),
    },
  ]

  return decisionTree.find(decision => decision.matches())?.action ?? null
}
